/**
 * Invite links for a group: `/groups/:id?invite=CODE`, and the one way into a group.
 *
 * WhatsApp's model (operator, 2026-09-23; enforced in the database by migration 128): each group has ONE link any
 * member can share, a manager can reset it, and a signed-in visitor holding the live code joins in one tap unless
 * the group requires approval. Without a code - someone who found the group by search - joining a private group is
 * a request the creator or a manager approves. Codes are minted and checked only by database functions; the
 * group_invites table is no longer readable to non-members.
 */

export function groupInvitePath(groupId, code) {
  return `/groups/${groupId}?invite=${encodeURIComponent(code)}`;
}

/** The group's live link code (created on first ask). Members only; null if it could not be had. */
export async function fetchGroupInviteCode(supabase, groupId) {
  if (!groupId) return null;
  const { data, error } = await supabase.rpc('group_invite_link', { p_group_id: groupId });
  if (error) {
    console.error('[groupInviteLink] group_invite_link failed:', error);
    return null;
  }
  return data || null;
}

/**
 * The invite's message: short, and the link follows on its own (Sam, 2026-09-28 - "Join my group for <event>").
 * The event name comes from the group's event row (override, then display name, then raw name).
 */
export function groupInviteText(event) {
  const eventName =
    event?.display_name_override || event?.display_name || event?.event_name || null;
  return eventName ? `Join my group for ${eventName}` : 'Join my group';
}

/** What to share: the live invite link for a member (the plain group page otherwise), and its text. */
export async function buildGroupInviteShare(supabase, { groupId, isMember, event }) {
  let path = `/groups/${groupId}`;
  if (isMember) {
    const code = await fetchGroupInviteCode(supabase, groupId);
    if (code) path = groupInvitePath(groupId, code);
  }
  return { path, text: groupInviteText(event) };
}

/** Manager only: revoke the live link and issue a new one. */
export async function resetGroupInviteCode(supabase, groupId) {
  const { data, error } = await supabase.rpc('reset_group_invite', { p_group_id: groupId });
  if (error) {
    console.error('[groupInviteLink] reset_group_invite failed:', error);
    return null;
  }
  return data || null;
}

/** Does this code let the viewer straight in? False on any error, so the card falls back to "Request to join". */
export async function checkGroupInvite(supabase, groupId, code) {
  if (!groupId || !code) return false;
  const { data, error } = await supabase.rpc('check_group_invite', {
    p_group_id: groupId,
    p_code: code,
  });
  if (error) return false;
  return data === true;
}

/**
 * join_group: joins or requests, server-side. Returns the outcome string
 * (joined | already_member | requested | already_requested | full | closed | not_found | error).
 */
export async function joinGroupRpc(supabase, groupId, code = null) {
  const { data, error } = await supabase.rpc('join_group', {
    p_group_id: groupId,
    p_invite_code: code || null,
  });
  if (error) throw error;
  return data?.outcome ?? 'error';
}
